import {AsyncPipe, NgComponentOutlet} from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  Provider,
  runInInjectionContext,
  signal,
  Signal,
  Type,
  untracked,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {MatChipsModule} from '@angular/material/chips';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelect} from '@angular/material/select';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {SearchSort, SortOption} from '@app/search/search-bar/search-bar.config';
import {SearchExecutionConfig, SearchManagerService} from '@app/search/search-manager.service';
import {InstanceUserRoleID, UserResponse} from '@middleware';
import {
  GenericSearchParams,
  GenericSearchQuery,
  SearchBodyMap,
  SearchIndexName,
  SearchRecordType,
} from '@models/search';
import {TileInfo} from '@models/tile-info';
import {AuthDataService} from '@services/authentication/authentication-data.service';
import {SearchService} from '@services/search/search.service';
import {UserService} from '@services/user/user.service';
import {isEqual} from 'lodash';
import {debounceTime, from, Observable, of, startWith, switchMap} from 'rxjs';
import {Filter, FILTERS_ME_TOKEN, FILTERS_TOKEN, FilterVisibility, MeComponent} from '../filters/filter';

@Component({
  selector: 'app-search-bar',
  imports: [
    AsyncPipe,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatChipsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOption,
    MatSelect,
    NgComponentOutlet,
    ReactiveFormsModule,
  ],
  providers: [SearchManagerService],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
})
export class SearchBarComponent implements OnInit, AfterViewInit {
  @Input() recordType!: SearchRecordType;
  @Input() index!: SearchIndexName;
  @Input() sort: SortOption[];
  @Output() tileData = new EventEmitter<TileInfo[]>();
  @Output() recordChange = new EventEmitter<SearchRecordType>();
  @Output() indexChange = new EventEmitter<SearchIndexName>();
  @Output() loadingChange = new EventEmitter<boolean>();
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  currentPage = 1;
  perPage = 100;

  filtersAreLoading: WritableSignal<boolean> = signal(false);
  query: WritableSignal<GenericSearchQuery<SearchIndexName>> = signal(undefined);
  searchSort: WritableSignal<SearchSort> = signal(undefined);
  searchString: WritableSignal<string> = signal('');
  readonly user: Signal<UserResponse | null | undefined>;

  autocompleteOptions: Observable<string[]>;
  filters: Filter<SearchIndexName>[] = [];
  me: Type<MeComponent>;
  queryTermControl = new FormControl('');
  sortControl: FormControl<SearchSort>;
  cachedParams: {[key: string]: any} = {};

  // General method to check if a filter has any selection
  isFilterActive(filter: Filter<SearchIndexName>): boolean {
    // Check if the filter is active OR if the user has manually opened it
    const hasSelection = filter.selectedValues().length > 0;
    const isDefaultExpanded = filter.defaultExpanded === true;
    const isManual = filter['isManuallyExpanded'] === true;

    // The panel should be expanded if:
    //  - it is set to expand by default (on load)
    //  - there's a selection (on load)
    //  - the user manually opened it.
    return isDefaultExpanded || hasSelection || isManual;
  }

  // Set the manual expansion flag on the Filter instance
  setFilterExpanded(filter: Filter<SearchIndexName>, isExpanded: boolean): void {
    filter['isManuallyExpanded'] = isExpanded;

    // Call syncFilterUI if it's expanding for the first time
    if (isExpanded) {
      this.syncFilterUI(filter);
    }
  }

  // Method to safely call the sync function on the view component of ANY filter
  syncFilterUI(filter: Filter<SearchIndexName>): void {
    // Check if the filter instance exists and has the forceSyncUI method
    // Note: optional chaining explicitly NOT used here, to avoid ambiguity
    if (filter && filter['forceSyncUI']) {
      filter['forceSyncUI']();
    }
  }

  // Only perform a search if the entire search context is stable
  private async ensureSearchContextReady() {
    if (this.authData.isUserLoggedIn() && !this.authData.relevantOrgId()) {
      await this.authData.loadOrganizations();
    }
  }

  constructor(
    private readonly searchService: SearchService,
    private readonly searchManager: SearchManagerService,
    private readonly userService: UserService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    protected injector: Injector,
    protected authData: AuthDataService,
  ) {
    this.user = this.userService.userSignal;
    this.autocompleteOptions = this.queryTermControl.valueChanges.pipe(
      debounceTime(250),
      switchMap(term => {
        const searchTerm = term ?? ''; // This fallback is to cover null or undefined values
        return searchTerm.length < 2
          ? of([])
          : from(
              this.searchService.getAutocompleteResults({
                term: searchTerm,
                record_type: this.recordType,
                max_suggestions: 5,
                max_phrase_length: 5,
              }),
            );
      }),
      startWith([]),
    );

    this.filtersAreLoading.set(true);

    // Watch for changes to the filter controls
    effect(async () => {
      // Watch for changes to search string, sort, and filters
      const newParams = {
        q: this.searchString(),
        sort: this.searchSort(),
      };

      for (let filter of this.filters) {
        const filterValues = filter.selectedValues();

        if (filterValues) {
          // Filter out empty values
          newParams[filter.key] = filterValues.filter(val => ![null, undefined, '', 'null', 'undefined'].includes(val));
        }
      }

      // Serialize & deserialize with JSON so we're not comparing a ParamMap to a plain object
      const oldParams = this.cachedParams || JSON.parse(JSON.stringify(this.route.snapshot.queryParamMap));
      const differentParams = !isEqual(oldParams, {params: newParams});
      const filtersDoneLoading = untracked(() => !this.filtersAreLoading());

      if (differentParams && filtersDoneLoading) {
        this.cachedParams = {params: newParams};
        await this.router.navigate([], {queryParams: newParams});
      }
    });

    effect(() => {
      this.loadingChange.emit(this.searchManager.loading());
    });
  }

  private _publicView?: boolean;
  @Input()
  set publicView(value: boolean | undefined) {
    this._publicView = value;
  }
  // Parent component can explicitly set publicView. If not set, check if there's a logged-in user.
  get publicView(): boolean {
    return this._publicView ?? this.user() == null;
  }

  triggerSearch(): void {
    const newSearchString = this.queryTermControl.value;

    // Update the searchString signal. This is what currently initiates the search pipeline
    // via the `effect` block that watches `this.searchString()`.
    this.searchString.set(newSearchString);

    // Keep the search term filter in sync
    const searchTermFilter = this.filters.find(f => f.key === 'q');
    if (searchTermFilter) {
      searchTermFilter.load(newSearchString ? [newSearchString] : []);
    }

    // Close the autocomplete panel when a search is executed
    this.autocompleteTrigger?.closePanel();

    // Check if a non-empty search term is being applied.
    const isSearchTermPresent = newSearchString && newSearchString.trim().length > 0;
    const currentSort = this.searchSort();

    // If a search term is present and the current sort is NOT 'relevance', force the sort to 'relevance'.
    if (isSearchTermPresent && currentSort !== 'relevance') {
      this.sortControl.setValue('relevance');
      this.searchSort.set('relevance');
    }
  }

  ngOnInit(): void {
    // Set the sort default:
    const defaultSort = this.sort?.[0]?.key;
    this.sortControl = new FormControl(defaultSort);

    runInInjectionContext(this.injector, () => {
      const filterTypes = this.injector.get<Type<Filter<SearchIndexName>>[]>(FILTERS_TOKEN);
      this.injector = Injector.create({
        providers: filterTypes as Provider[],
        parent: this.injector,
        name: 'SearchBarComponentInjector',
      });
      this.me = <Type<MeComponent>>this.injector.get(FILTERS_ME_TOKEN, null);
      this.filters = filterTypes.map(
        this.injector.get.bind(this.injector) as (clazz: Type<Filter<SearchIndexName>>) => Filter<SearchIndexName>,
      );

      this.searchSort.set(defaultSort);
      this.sortControl.valueChanges.subscribe(v => this.searchSort.set(v));

      // Watch for changes to the query parameters in the URL
      this.route.queryParamMap.subscribe(params => {
        this.performSearch(params);
      });
    });
  }

  async performSearch(params: ParamMap) {
    this.filtersAreLoading.set(true);
    await this.ensureSearchContextReady();

    if (params.keys.length === 0) {
      const defaultParams: Record<string, string | string[]> = {
        q: '',
        resource_type: ['generic', 'future_event'],
      };

      if (this.authData.isUserLoggedIn()) {
        defaultParams['audience'] = [this.authData.relevantOrgId() || 'undefined'];
      }

      /** TODO: Investigate the duplicate initial search. Returning here avoids
       searching twice, since router.navigate() triggers performSearch() again via
       queryParamMap. Eventually, SearchManager should own queryParam-driven searches. */
      await this.router.navigate([], {
        queryParams: defaultParams,
      });
      return;
    }
    this.queryTermControl?.setValue(params.get('q'));
    this.searchString.set(params.get('q') ?? '');

    if (params.keys.includes('sort')) {
      this.sortControl.setValue(<SearchSort>params.get('sort'));
      this.sortControl.markAsTouched();
    }

    for (let key of params.keys) {
      const filter = this.filters.find(f => f.key == key);
      if (filter) filter.load(params.getAll(key));
    }

    // If an 'index' param is present, update the search bar's index and recordType
    if (params.keys.includes('index')) {
      const indexValue = params.get('index') as SearchIndexName;
      if (indexValue) {
        this.index = indexValue;
        this.recordType = indexValue.toLowerCase() as any;
        this.indexChange.emit(indexValue);
      }
    }

    this.filtersAreLoading.set(false);
    await this.searchInitialResults();
  }

  ngAfterViewInit(): void {
    // Check if the element exists before trying to focus it
    if (this.searchInput) {
      // Use setTimeout to ensure the focus happens after the current change detection cycle
      // and any potential rendering from Angular Material.
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 0);
    }
  }

  private buildSearchConfig(): SearchExecutionConfig {
    return {
      body: this.buildSearchBody(),
      params: this.buildSearchParams(),
      index: this.index,
      perPage: this.perPage,
      queryKey: JSON.stringify({
        q: this.searchString() ?? '',
        sort: this.searchSort(),
        filters: this.filters.map(f => ({
          key: f.key,
          vals: f.selectedValues(),
        })),
      }),
    };
  }

  private buildSearchBody<K extends SearchIndexName>(): SearchBodyMap[K] {
    let global_search = this.searchString();
    if (!global_search?.trim()) global_search = '*';
    const filterBody: SearchBodyMap[K] = {global_search};

    for (let filter of this.filters) {
      filter.apply(filterBody);
    }

    return filterBody;
  }

  private buildSearchParams(): GenericSearchParams {
    const baseParams: GenericSearchParams = {
      public_view: this.publicView,
      page: this.currentPage,
      per_page: this.perPage,
    };

    let params: GenericSearchParams = baseParams;

    // Append sort options
    switch (this.searchSort()) {
      case 'most_favorited':
        return {
          ...params,
          favorite_count_sort_order: 'desc', // Descending: from most to least
        };
      case 'recently_updated':
        return {
          ...params,
          time_updated_sort_order: 'desc', // Descending: from more recent
        };
      default:
        return params;
    }
  }

  tiles: TileInfo[] = [];

  get hasFilters(): boolean {
    return this.filters.reduce((acc, f) => f.selectedValues().length + acc, 0) > 0;
  }

  async searchInitialResults() {
    await this.searchManager.executeSearch(this.buildSearchConfig());
    this.tileData.emit(this.searchManager.results());
  }

  async loadNextPage() {
    await this.searchManager.loadNextPage(this.buildSearchConfig());
    this.tileData.emit(this.searchManager.results());
  }

  get visibleFilters(): Filter<SearchIndexName>[] {
    return this.filters.filter(
      filter =>
        filter.visibility == FilterVisibility.EVERYONE ||
        (filter.visibility == FilterVisibility.ONLY_AUTHED_USERS && this.authData.isUserLoggedIn()) ||
        (filter.visibility == FilterVisibility.ONLY_ADMIN_USERS &&
          this.authData.me()?.instance_user_role_id == InstanceUserRoleID.ADMIN),
    );
  }

  clearAllFilters() {
    this.filters.forEach(f => {
      // Reset the filter's selected values (this updates the signal)
      f.load([]);

      // Force the filter UI (CategoryView) to sync with the new empty selection
      // This calls CategoryFilter.forceSyncUI() -> CategoryView.forceSyncUI()
      this.syncFilterUI(f);
    });
  }

  public getCurrentSearchConfig(): SearchExecutionConfig {
    return this.buildSearchConfig();
  }
}
